import React, { FC, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PSPDFKit from '@nutrient-sdk/viewer';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import { configService } from '@web/services/singletons';
import { Constants } from '@packages/core/config';
import { useTranslation } from 'react-i18next';

const logger = getLogger('vin-sessions');

interface Props {
    title: string;
    url: string | null;
    onHide: () => void;
    onDownload: () => void;
}

const TOOLBAR_ITEMS = ['zoom-in', 'zoom-out', 'zoom-mode', 'pager', 'search', 'print', 'export-pdf'];

export const PdfPreviewModal: FC<Props> = ({ title, url, onHide, onDownload }) => {
    const { t } = useTranslation();
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [hasFailed, setHasFailed] = useState(false);

    useEffect(() => {
        setHasFailed(false);
    }, [url]);

    useEffect(() => {
        if (!container || !url) {
            return;
        }
        PSPDFKit.load({
            container,
            disableWebAssemblyStreaming: true,
            document: url,
            licenseKey: configService.get(Constants.Env.NutrientApiKey),
            baseUrl: `${window.location.protocol}//${window.location.host}/`,
            editableAnnotationTypes: [],
            toolbarItems: PSPDFKit.defaultToolbarItems.filter((item) => TOOLBAR_ITEMS.includes(item.type)),
        }).catch((error) => {
            logger.error('Failed to load the PDF preview', { error });
            setHasFailed(true);
        });
        return () => {
            PSPDFKit.unload(container);
        };
    }, [container, url]);

    return (
        <Modal show={Boolean(url)} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ height: '80vh', padding: 0 }}>
                {hasFailed ? (
                    <div
                        className="d-flex flex-column align-items-center justify-content-center h-100"
                        style={{ gap: 12, padding: 24, textAlign: 'center' }}
                    >
                        <Icon name="download" size={40} color="blueOne" />
                        <Typography style={{ fontWeight: 600, fontSize: 16 }}>
                            {t(
                                'stisPublications:documentInfo.fileActions.previewFailedTitle',
                                "This file can't be previewed"
                            )}
                        </Typography>
                        <Typography style={{ fontSize: 14, color: '#4C525D' }}>
                            {t(
                                'stisPublications:documentInfo.fileActions.previewFailedMessage',
                                'Download it to open it on your computer.'
                            )}
                        </Typography>
                        <Button
                            variant="primary"
                            onPress={onDownload}
                            title={t('stisPublications:documentInfo.fileActions.download', 'Download')}
                        />
                    </div>
                ) : (
                    <div ref={setContainer} style={{ width: '100%', height: '100%' }} />
                )}
            </Modal.Body>
        </Modal>
    );
};
